import { unwrap, type PermissionsOut } from "@sagot/api-client";
import type { PermissionKey } from "@sagot/constants";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useParams } from "react-router";
import { queryKeys } from "../lib/query-keys";
import { useApi } from "./session-provider";
import { useWorkspaceId } from "./workspace-context";

/** Where a permission must hold (DEC-020 nav semantics). */
export interface PermissionScope {
  /** Check on this agent (agent-scoped assignments count). */
  agentId?: string | null | undefined;
  /** Held on any assignment, whatever its scope (workspace pages that filter rows by scope). */
  anywhere?: boolean;
}

export interface PermissionsState {
  isPending: boolean;
  data: PermissionsOut | undefined;
  readOnly: boolean;
  isOwner: boolean;
  can: (permission: PermissionKey, scope?: PermissionScope) => boolean;
}

export function evaluate(data: PermissionsOut | undefined, permission: PermissionKey, scope: PermissionScope = {}): boolean {
  if (!data) return false;
  if (scope.agentId) return (data.agent ?? []).includes(permission);
  if (scope.anywhere) return data.anywhere.includes(permission);
  return data.workspace.includes(permission);
}

/** Agent from the current route (`/w/:ws/agents/:agentId/...`). */
export function useRouteAgentId(): string | null {
  return useParams().agentId ?? null;
}

/** `GET /v1/me/permissions?agent_id=`: the server's effective permissions (UI mirrors, API enforces). */
export function usePermissions(agentId: string | null = null): PermissionsState {
  const api = useApi();
  const wsId = useWorkspaceId();
  const query = useQuery({
    queryKey: queryKeys.permissions(wsId, agentId),
    queryFn: () => unwrap(api.GET("/v1/me/permissions", { params: { query: agentId ? { agent_id: agentId } : {} } })),
    staleTime: 30_000,
  });
  return {
    isPending: query.isPending,
    data: query.data,
    readOnly: query.data?.read_only ?? false,
    isOwner: query.data?.is_owner ?? false,
    can: (permission, scope) => evaluate(query.data, permission, scope),
  };
}

interface CanProps extends PermissionScope {
  permission: PermissionKey;
  children: ReactNode;
  /** Rendered when the permission is missing (e.g. a disabled control with a tooltip). */
  fallback?: ReactNode;
}

/** Permission-gated rendering: hides (or swaps in `fallback`) unless the server grants it. */
export function Can({ permission, agentId, anywhere, children, fallback = null }: CanProps) {
  const permissions = usePermissions(agentId ?? null);
  if (permissions.isPending) return null;
  return permissions.can(permission, { agentId, anywhere }) ? children : fallback;
}
